import cloneDeep from 'clone-deep'
import { IVisibleLogic } from 'config'
import { InputVisibleLogic, Toggle } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import {
  defaultLoanApplicationDateRules,
  ILoanApplicationDateRuleConfig,
  LoanApplicationDateRuleKey,
  loanApplicationDateRuleLabels,
  loanApplicationDateRuleOrder,
  TLoanApplicationDateRulesConfig,
} from './types'

interface IProps {
  value?: Partial<TLoanApplicationDateRulesConfig>
  loanGlobalStatus: Record<string, string>
  loanSubStatusOptions: Record<string, string>
  onChange: (value: TLoanApplicationDateRulesConfig) => void
  applyTridRules: boolean
  onApplyTridRulesChange: (enabled: boolean) => void
  tridRulesVisibleLogic?: IVisibleLogic[][]
  onTridRulesVisibleLogicChange: (value: IVisibleLogic[][]) => void
}

export const LoanApplicationDateRules = ({
  value,
  loanGlobalStatus,
  loanSubStatusOptions,
  onChange,
  applyTridRules,
  onApplyTridRulesChange,
  tridRulesVisibleLogic,
  onTridRulesVisibleLogicChange,
}: IProps) => {
  const config: TLoanApplicationDateRulesConfig = {
    ...defaultLoanApplicationDateRules(),
    ...value,
  }

  const updateRule = (key: LoanApplicationDateRuleKey, patch: Partial<ILoanApplicationDateRuleConfig>) => {
    const next = cloneDeep(config)
    next[key] = { ...next[key], ...patch }
    onChange(next)
  }

  const renderRule = (key: LoanApplicationDateRuleKey) => {
    const rule = config[key]
    const tieType = rule.tieType || 'status'

    return (
      <div className="border rounded-md p-4 mb-4 bg-white" key={key}>
        <div className="flex items-start justify-between gap-4 mb-3">
          <p className="text-sm font-semibold flex-1">{loanApplicationDateRuleLabels[key]}</p>
          <Toggle
            id={`loan-application-date-rule-${key}`}
            title="Enabled"
            value={!!rule.enabled}
            onChange={(enabled: boolean) => updateRule(key, { enabled })}
          />
        </div>

        <InputVisibleLogic
          id={`loan-application-date-rule-${key}-logic`}
          title="Visible Conditions"
          value={rule.visibleLogic || []}
          options={{ hasNotVisible: true }}
          onChange={(visibleLogic) => updateRule(key, { visibleLogic })}
        />

        {key === 'statusSubStatus' && rule.enabled && (
          <div className="mt-4 grid grid-cols-1 gap-4">
            <RenderInput
              Key={`${key}-tieType`}
              input={{
                inputType: 'radio',
                title: 'Tie to',
                span: 'full',
                options: {
                  status: 'Status',
                  subStatus: 'Sub Status',
                },
                value: tieType,
              }}
              onChange={(_k: string, tieType: string) =>
                updateRule(key, { tieType: tieType as 'status' | 'subStatus' })
              }
            />
            {tieType === 'status' && (
              <RenderInput
                Key={`${key}-tieStatuses`}
                input={{
                  inputType: 'multiselect',
                  title: 'Status',
                  options: loanGlobalStatus,
                  defaultSelected: false,
                  span: 'full',
                  value: rule.tieStatuses || [],
                }}
                onChange={(_k: string, tieStatuses: string[]) => updateRule(key, { tieStatuses })}
              />
            )}
            {tieType === 'subStatus' && (
              <RenderInput
                Key={`${key}-tieSubStatuses`}
                input={{
                  inputType: 'multiselect',
                  title: 'Sub Status',
                  options: loanSubStatusOptions,
                  defaultSelected: false,
                  span: 'full',
                  value: rule.tieSubStatuses || [],
                }}
                onChange={(_k: string, tieSubStatuses: string[]) => updateRule(key, { tieSubStatuses })}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="col-span-full mt-2">
      <RenderInput
        Key="loan-application-date-section"
        input={{
          inputType: 'section',
          title: 'Loan Application Date',
          span: 'full',
        }}
        onChange={() => {}}
      />
      <p className="text-sm text-gray-600 mb-4">
        Enable one or more rules below. When a trigger event occurs, the first enabled rule whose visible conditions
        match the loan will set the Application Date (if not already set). Empty visible conditions mean the rule
        applies to all loans.
      </p>
      {loanApplicationDateRuleOrder.map((key) => renderRule(key))}
      <div className="border rounded-md p-4 mb-4 bg-white">
        <div className="flex items-start justify-between gap-4 mb-3">
          <p className="text-sm font-semibold flex-1">Apply TRID Rules</p>
          <Toggle
            id="apply-trid-rules-enabled"
            title="Enabled"
            value={applyTridRules}
            onChange={onApplyTridRulesChange}
          />
        </div>
        <InputVisibleLogic
          id="apply-trid-rules-visible-logic"
          title="Visible Conditions"
          value={tridRulesVisibleLogic || []}
          options={{ hasNotVisible: true }}
          onChange={onTridRulesVisibleLogicChange}
        />
      </div>
    </div>
  )
}

export * from './types'
